import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { Alert, DialogContent, Button } from '@mui/material'
import Grid from '@mui/material/Grid'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { useAddGeneralBillingMutation } from 'api/settings/addGeneralBilling'
import { PAYMENT_TERMS, PAYMENT_TERMS_OPTIONS } from 'components/customer-details/content/payment-terms/settings'
import { ERROR_EXCEPTIONS } from 'settings/error'
import { CACHE_TAG_SETTINGS_BILLING_GENERAL_LIST } from 'api/cacheTagTypes'

import T from 'T'
import api from 'api'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import HHFormTextField from 'components/form-fields/v5/HHFormTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import { HHFormNumberField } from 'components/form-fields/v5'

const { NET, DUE_ON_RECEIPT } = PAYMENT_TERMS
const { VALUE_ALREADY_EXIST_EXCEPTION } = ERROR_EXCEPTIONS

const CreatePaymentTermsDialog = ({ isOpen = false, onSuccess = noop, onClose }) => {
  const dispatch = useDispatch()
  const [addGeneralBilling, { isLoading: isCreateLoading }] = useAddGeneralBillingMutation()
  const [isAlreadyExist, setIsAlreadyExist] = useState(false)
  const createPaymentTermsForm = useForm({ defaultValues: { term: '', interval: '' } })

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = createPaymentTermsForm

  const watchTerm = watch('term')
  const isNetTermSelected = watchTerm === NET
  const termHelperText = get(errors, 'term.message')
  const intervalHelperText = get(errors, 'interval.message')

  const handleChange = value => {
    if (value === DUE_ON_RECEIPT) {
      setValue('interval', 0)
    }
    if (value === NET) {
      setValue('interval', '')
    }
  }

  const onSubmitHandler = data => {
    setIsAlreadyExist(false)
    const newTerm = data?.term
    const newInterval = get(data, 'interval', 0)
    const payload = { billingCycle: [], paymentTerm: [{ term: newTerm, interval: newInterval }] }
    addGeneralBilling(payload)
      .unwrap()
      .then(response => {
        toast.success('Payment terms successfully created')
        dispatch(api.util.invalidateTags([CACHE_TAG_SETTINGS_BILLING_GENERAL_LIST]))
        // Currently backend is returning all data, return only added data
        const addedPaymentTerm = get(response, 'paymentTerm', []).find(pt => pt.term === newTerm && pt.interval === Number(newInterval))
        onSuccess(get(addedPaymentTerm, 'id', ''))
        onClose()
      })
      .catch(error => {
        if (get(error, 'data.exceptionType') === VALUE_ALREADY_EXIST_EXCEPTION) {
          setIsAlreadyExist(true)
          return
        }
        handleError(error)
      })
  }

  useEffect(() => {
    if (isOpen) {
      reset()
      setIsAlreadyExist(false)
    }
  }, [isOpen])

  return (
    <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <HHDialogTitle title={T.CREATE_PAYMENT_TERMS} onClose={onClose} />
      <DialogContent>
        <FormProvider {...createPaymentTermsForm}>
          <Grid container mt={2} columnGap={2}>
            <Grid size="grow">
              <HHFormTextField
                required
                error={termHelperText}
                helperText={termHelperText}
                control={control}
                rules={{ required: T.CANNOT_BE_EMPTY }}
                name="term"
                label="Payment term type"
                fullWidth
                deprecatedLabel={false}
                select
                onChange={handleChange}
              >
                {PAYMENT_TERMS_OPTIONS.map(({ label, value }) => (
                  <SelectItem value={value}>{label}</SelectItem>
                ))}
              </HHFormTextField>
            </Grid>
            {isNetTermSelected && (
              <Grid size={3}>
                <HHFormNumberField
                  required
                  error={intervalHelperText}
                  helperText={intervalHelperText}
                  control={control}
                  rules={{ required: T.CANNOT_BE_EMPTY, min: { value: 1, message: T.CANNOT_BE_ZERO } }}
                  name="interval"
                  label="Days"
                  placeholder=""
                  deprecatedLabel={false}
                  allowNegative={false}
                  thousandSeparator={false}
                  decimalScale={0}
                />
              </Grid>
            )}
          </Grid>
          {isAlreadyExist && (
            <Alert sx={{ mt: 2 }} severity="error">
              Payment term already exists
            </Alert>
          )}
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        <CancelButton size="medium" onClick={onClose} />
        <Button loading={isCreateLoading} size="small" variant="contained" onClick={handleSubmit(onSubmitHandler)}>
          {T.CREATE_NEW}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

CreatePaymentTermsDialog.propTypes = {
  isOpen: PropTypes.bool,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
}

export default CreatePaymentTermsDialog
